import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { useNativeChrome } from '../hooks/useNativeChrome';
import { useAuth } from '../context/AuthContext';
import { BrandMark } from '../components/ui/bits';
import {
  AuthHeader,
  AuthPage,
  AuthTitle,
  ErrorNote,
  OrDivider,
  PasswordField,
  PrimaryButton,
  SocialButtons,
  TextField,
  authMessage
} from '../components/auth/parts';
import { Mail } from '../components/ui/icons';

export default function Login() {
  useNativeChrome();
  const navigate = useNavigate();
  const { signIn, signInWithGoogle, signInWithApple } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Social sign-in ships in the native apps only; the web uses email for now.
  const isNative = Capacitor.isNativePlatform();

  async function run(fn: () => Promise<void>) {
    setError(null);
    setBusy(true);
    try {
      await fn();
      // On success the app gate renders the game.
    } catch (err) {
      setError(authMessage(err));
      setBusy(false);
    }
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    run(() => signIn(email.trim(), password));
  }

  return (
    <AuthPage>
      <AuthHeader backLabel="Welcome" onBack={() => navigate('/welcome')} />

      <div className="flex flex-1 flex-col">
        <BrandMark size={44} className="mt-2" />
        <div className="mt-4">
          <AuthTitle
            title="Welcome back"
            subtitle="Pick up your ladder right where you left off."
          />
        </div>

        {isNative && (
          <>
            <div className="mt-6">
              <SocialButtons
                disabled={busy}
                onApple={() => run(signInWithApple)}
                onGoogle={() => run(signInWithGoogle)}
              />
            </div>

            <div className="my-5">
              <OrDivider />
            </div>
          </>
        )}

        <form onSubmit={onSubmit} className={`flex flex-col gap-4 ${isNative ? '' : 'mt-6'}`}>
          <TextField
            label="Email"
            type="email"
            inputMode="email"
            value={email}
            onChange={setEmail}
            placeholder="you@example.com"
            autoComplete="email"
            prefix={<Mail width={18} height={18} />}
          />
          <PasswordField
            value={password}
            onChange={setPassword}
            autoComplete="current-password"
            trailingLabel={
              <button
                type="button"
                onClick={() => navigate('/reset')}
                className="text-[12px] font-bold text-accent-ink"
              >
                Forgot?
              </button>
            }
          />

          {error && <ErrorNote>{error}</ErrorNote>}

          <div className="mt-1">
            <PrimaryButton type="submit" busy={busy} disabled={busy}>
              Log in
            </PrimaryButton>
          </div>
        </form>

        <div className="flex-1" />

        <p className="pb-2 pt-5 text-center text-[14px] font-medium text-ink2">
          New to HeadToHead?{' '}
          <button type="button" onClick={() => navigate('/signup')} className="font-bold text-accent-ink">
            Create an account
          </button>
        </p>
      </div>
    </AuthPage>
  );
}
